import { createContext, useContext, useState, useCallback } from "react";
import { AppModal } from "@/components/ui/AppModal";
import { getModalDefaults, type ModalKind, type DismissBehavior } from "@/lib/modalRegistry";
import { captureEvent } from "@/lib/posthog";

export interface OpenModalOptions {
  title?: string;
  body?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
  dismissBehavior?: DismissBehavior;
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  trigger?: string;
  onPrimary?: () => void;
  onSecondary?: () => void;
  onClose?: () => void;
}

interface ModalState {
  kind: ModalKind;
  title: string;
  body: string;
  primaryLabel: string;
  secondaryLabel?: string;
  dismissBehavior: DismissBehavior;
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  onPrimary?: () => void;
  onSecondary?: () => void;
  onClose?: () => void;
}

interface ModalContextValue {
  openModal: (kind: ModalKind, options?: OpenModalOptions) => void;
  closeModal: () => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [modal, setModal] = useState<ModalState | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const openModal = useCallback((kind: ModalKind, options: OpenModalOptions = {}) => {
    const defaults = getModalDefaults(kind);
    setModal({
      kind,
      title: options.title ?? defaults.defaultTitle,
      body: options.body ?? defaults.defaultBody,
      primaryLabel: options.primaryLabel ?? defaults.primaryLabel,
      secondaryLabel: options.secondaryLabel ?? defaults.secondaryLabel,
      dismissBehavior: options.dismissBehavior ?? defaults.dismissBehavior,
      size: options.size,
      icon: options.icon,
      onPrimary: options.onPrimary,
      onSecondary: options.onSecondary,
      onClose: options.onClose,
    });
    setIsOpen(true);
    captureEvent("modal_shown", { modalKind: kind, trigger: options.trigger ?? "unknown" });
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => setModal(null), 200);
  }, []);

  const handleClose = (method: "escape" | "backdrop" | "x_button" = "x_button") => {
    if (modal) {
      captureEvent("modal_dismissed", { modalKind: modal.kind, method });
    }
    modal?.onClose?.();
    closeModal();
  };

  const handlePrimary = () => {
    if (modal) {
      captureEvent("modal_primary_clicked", { modalKind: modal.kind });
    }
    modal?.onPrimary?.();
    closeModal();
  };

  const handleSecondary = () => {
    if (modal) {
      captureEvent("modal_secondary_clicked", { modalKind: modal.kind });
    }
    modal?.onSecondary?.();
    closeModal();
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {modal && (
        <AppModal
          kind={modal.kind}
          isOpen={isOpen}
          title={modal.title}
          body={modal.body}
          icon={modal.icon}
          primaryLabel={modal.primaryLabel}
          onPrimary={handlePrimary}
          secondaryLabel={modal.secondaryLabel}
          onSecondary={handleSecondary}
          onClose={(method) => handleClose(method ?? "x_button")}
          dismissBehavior={modal.dismissBehavior}
          size={modal.size}
        />
      )}
    </ModalContext.Provider>
  );
}

export function useModalContext(): ModalContextValue {
  const ctx = useContext(ModalContext);
  if (!ctx) {
    throw new Error("useModalContext must be used inside <ModalProvider>");
  }
  return ctx;
}
